import Server from './server';
import Chunk from './chunk';
import { ajax as Ajax, Type } from 'main.core';
import AbstractUploadController from './abstract-upload-controller';
import UploaderError from '../uploader-error';

export default class UploadController extends AbstractUploadController
{
	file: File = null;
	chunkOffset: ?number = null;
	chunkTimeout: ?number = null;
	token: string = null;
	xhr: XMLHttpRequest = null;

	constructor(server: Server)
	{
		super(server);
	}

	upload(file: File): void
	{
		if (this.chunkOffset !== null)
		{
			return;
		}

		this.file = file;

		const nextChunk = this.#getNextChunk();
		if (nextChunk)
		{
			this.#uploadChunk(nextChunk);
		}
	}

	abort(): void
	{
		if (this.xhr)
		{
			this.xhr.abort();
			this.xhr = null;
		}

		clearTimeout(this.chunkTimeout);
	}

	#uploadChunk(chunk: Chunk)
	{
		const totalSize = this.getFile().size;
		const isOnlyOneChunk = chunk.getOffset() === 0 && totalSize === chunk.getSize();

		let fileName = this.getFile().name;
		if (fileName.normalize)
		{
			fileName = fileName.normalize();
		}

		const headers = [
			{ name: 'Content-Type', value: this.getFile().type },
			{ name: 'X-Upload-Content-Name', value: encodeURIComponent(fileName) },
		];

		if (!isOnlyOneChunk)
		{
			const rangeStart = chunk.getOffset();
			const rangeEnd = chunk.getOffset() + chunk.getSize() - 1;
			const rangeHeader = `bytes ${rangeStart}-${rangeEnd}/${totalSize}`;

			headers.push({ name: 'Content-Range', value: rangeHeader });
		}

		const controllerOptions = this.getServer().getControllerOptions();
		Ajax.runAction('ui.fileuploader.upload', {
				headers,
				data: chunk.getData(),
				preparePost: false,
				getParameters: {
					controller: this.getServer().getController(),
					controllerOptions: controllerOptions ? JSON.stringify(controllerOptions) : null,
					token: this.getToken() || '',
				},
				onrequeststart: (xhr) => {
					this.xhr = xhr;
				},
				onprogressupload: (event: ProgressEvent) => {
					if (event.lengthComputable)
					{
						const size = this.getFile().size;
						const uploadedBytes = Math.min(size, chunk.getOffset() + event.loaded);
						const progress = size > 0 ? Math.floor(uploadedBytes / size * 100) : 100;
						this.emit('onProgress', { progress });
					}
				},
			})
			.then(response => {
				console.log('response', response);

				if (response.data.token)
				{
					this.setToken(response.data.token);

					const size = this.getFile().size;
					const progress = size > 0 ? Math.floor((chunk.getOffset() + chunk.getSize()) / size * 100) : 100;
					this.emit('onProgress', { progress });

					const nextChunk = this.#getNextChunk();
					if (nextChunk)
					{
						this.#uploadChunk(nextChunk);
					}
					else
					{
						this.emit('onProgress', { progress: 100 });
						this.emit('onUpload', { fileInfo: response.data.file });
					}
				}
				else
				{
					this.emit('onError', { error: new UploaderError('SERVER_ERROR') });
				}
			})
			.catch(response => {
				console.log('error', response);
				const error = UploaderError.createFromAjaxErrors(response.errors);
				const shouldRetry = error.getCode() === 'NETWORK_ERROR';

				if (!shouldRetry || !this.#retryUploadChunk(chunk))
				{
					this.emit('onError', { error });
				}
			})
		;
	}

	#retryUploadChunk(chunk: Chunk): boolean
	{
		const nextDelay = chunk.getNextRetryDelay();
		if (nextDelay === null)
		{
			return false;
		}

		clearTimeout(this.chunkTimeout);

		this.chunkTimeout = setTimeout(() => {
			this.#uploadChunk(chunk);
		}, nextDelay);

		return true;
	}

	#getNextChunk(): ?Chunk
	{
		if (this.getChunkOffset() !== null && this.getChunkOffset() >= this.getFile().size)
		{
			// End of File
			return null;
		}

		if (this.getChunkOffset() === null)
		{
			// First call
			this.chunkOffset = 0;
		}

		let chunk: Chunk;
		if (this.getChunkOffset() === 0 && this.getFile().size <= this.getChunkSize())
		{
			chunk = new Chunk(this.getFile(), this.getChunkOffset());
			this.chunkOffset = this.getFile().size;
		}
		else
		{
			const currentChunkSize = Math.min(this.getChunkSize(), this.getFile().size - this.getChunkOffset());
			const nextOffset = this.getChunkOffset() + currentChunkSize;
			const fileRange = this.getFile().slice(this.getChunkOffset(), nextOffset);

			chunk = new Chunk(fileRange, this.getChunkOffset());
			this.chunkOffset = nextOffset;
		}

		chunk.setRetries([...this.getServer().getChunkRetryDelays()]);

		return chunk;
	}

	getFile(): File
	{
		return this.file;
	}

	getChunkSize(): number
	{
		return this.getServer().getChunkSize();
	}

	getChunkOffset(): number
	{
		return this.chunkOffset;
	}

	getToken(): ?string
	{
		return this.token;
	}

	setToken(token: string): void
	{
		if (Type.isStringFilled(token))
		{
			this.token = token;
		}
	}
}